import { Dispatch } from "react";
import { createSlice, PayloadAction, AnyAction } from '@reduxjs/toolkit';
import { ILoadableState } from "../definitions/ILoadableState";
import { client, Path } from "../App";
import { GetBankAccountName, AccountResponse, CMOption, CMOptionResponse, driverPay_source, ValidateRoutingNumber, RoutingServiceResponse, UpdateBankAccountInfo, UpdateBankAccountResponse, UpdateContributionOption, UpdateContributionOptionResponse } from "../DCR.dtos"
import { push } from "connected-react-router";

export enum AccountAction {
    ADD,
    EDIT,
    DELETE,
    NONE
}

export interface IAccountState extends ILoadableState {
    cmAccounts: AccountResponse;
    cmOptions: CMOptionResponse;
    addUpdateAccountResponse: UpdateBankAccountResponse;
    updateContributionOptionResponse: UpdateContributionOptionResponse;
    addUpdateAccount: driverPay_source;//check - it might not be required.
    routingResponse: RoutingServiceResponse;
    isOptionsLoading?: boolean;
    isOptionsLoaded?: boolean;
    errorMessage?: string;
    isRoutingServiceLoading?: boolean;
    isroutingServiceLoaded?: boolean;
    isAccountUpdating?: boolean;
    isAccountUpdated?: boolean;
    isUpdateAccountError?: boolean;
    updateAccountErrorMessage?: string;
    isContribOptionUpdating?: boolean;
    isContribOptionUpdated?: boolean;
    isUpdateContributionError?: boolean;
    updateContributionErrorMessage?: string;
}

const initialManageAccountState: IAccountState = {
    cmAccounts: {} as AccountResponse,
    cmOptions: {} as CMOptionResponse,
    addUpdateAccountResponse: {} as UpdateBankAccountResponse,
    updateContributionOptionResponse: {} as UpdateContributionOptionResponse,
    addUpdateAccount: {} as driverPay_source,
    routingResponse: {} as RoutingServiceResponse,
    isLoading: false,
    isLoaded: false,
    isOptionsLoading: false,
    isOptionsLoaded: false,
    isRoutingServiceLoading: false,
    isroutingServiceLoaded: false,
    isAccountUpdating: false,
    isAccountUpdated: false,
    isUpdateAccountError: false,
    updateAccountErrorMessage: "",
    isContribOptionUpdating: false,
    isContribOptionUpdated: false,
    isUpdateContributionError: false,
    updateContributionErrorMessage: ""
};

const slice = createSlice({
    name: "account",
    initialState: initialManageAccountState,
    reducers: {
        resetState: () => {
            return {
                ...initialManageAccountState
            }
        },
        requestManageAccounts: (state: IAccountState) => {
            return {
                ...state,
                isLoading: true,
                isLoaded: false,
                isUpdateAccountError: false,
                updateAccountErrorMessage: ""
            }
        },
        receiveManageAccounts: (state: IAccountState, action: PayloadAction<{ cmAccountInfoResponse: AccountResponse }>) => {
            const cmAccounts = action.payload.cmAccountInfoResponse;
            return {
                ...state,
                cmAccounts,
                isLoading: false,
                isLoaded: true,
                isAccountUpdating: false,
                isAccountUpdated: false,
                isRoutingServiceLoading: false,
                isRoutingServiceLoaded: false,
                error: false,
            }
        },
        receiveManageAccountsError: (state: IAccountState, action: PayloadAction<{ message: string }>) => {
            const { message } = action.payload;
            return {
                ...state,
                isLoading: false,
                isLoaded: true,
                error: true,
                errorMessage: message
            }
        },

        requestCMOptions: (state: IAccountState) => {
            return {
                ...state,
                isOptionsLoading: true,
                isOptionsLoaded: false
            }
        },
        receiveCMOptions: (state: IAccountState, action: PayloadAction<{ cmOptionResponse: CMOptionResponse }>) => {
            const cmOptions = action.payload.cmOptionResponse;
            return {
                ...state,
                cmOptions,
                isOptionsLoading: false,
                isOptionsLoaded: true,
            }
        },
        receiveCMOptionsError: (state: IAccountState, action: PayloadAction<{ message: string }>) => {
            const { message } = action.payload;
            return {
                ...state,
                isOptionsLoading: false,
                isOptionsLoaded: true,
                error: true,
                errorMessage: message
            }
        },
        requestRoutingValidation: (state: IAccountState) => {
            return {
                ...state,
                isRoutingServiceLoading: true,
                isroutingServiceLoaded: false
            }
        },
        receiveRoutingValidation: (state: IAccountState, action: PayloadAction<{ routingResponse: RoutingServiceResponse }>) => {
            const routingResponse = action.payload.routingResponse;
            return {
                ...state,
                routingResponse,
                isRoutingServiceLoading: false,
                isroutingServiceLoaded: true
            }
        },
        requestAccountUpdate: (state: IAccountState) => {
            return {
                ...state,
                isAccountUpdating: true,
                isAccountUpdated: false,
                isUpdateAccountError: false,
                updateAccountErrorMessage: ""
            }
        },
        receiveAccountUpdate: (state: IAccountState, action: PayloadAction<{ addUpdateAccountResponse: UpdateBankAccountResponse }>) => {
            const addUpdateAccountResponse = action.payload.addUpdateAccountResponse;
            return {
                ...state,
                addUpdateAccountResponse,
                isAccountUpdating: false,
                isAccountUpdated: true
            }
        },
        receiveAccountUpdateError: (state: IAccountState, action: PayloadAction<{ message: string }>) => {
            const { message } = action.payload;
            return {
                ...state,
                isAccountUpdating: false,
                isAccountUpdated: true,
                isUpdateAccountError: true,
                updateAccountErrorMessage: message
            }
        },
        requestResetErrorState: (state: IAccountState) => {
            return {
                ...state,
                isUpdateAccountError: false,
                updateAccountErrorMessage: "",
                isAccountUpdating: false,
                isAccountUpdated: false
            }
        },
        requestResetAccountState: () => {
            return {
                ...initialManageAccountState
            }
        },
        requestContribOptionUpdate: (state: IAccountState) => {
            return {
                ...state,
                isContribOptionUpdating: true,
                isContribOptionUpdated: false,
                isUpdateContributionError: false,
                updateContributionErrorMessage: ""
            }
        },
        receiveContribOptionUpdate: (state: IAccountState, action: PayloadAction<{ updateContributionOptionResponse: UpdateContributionOptionResponse }>) => {
            const updateContributionOptionResponse = action.payload.updateContributionOptionResponse;
            return {
                ...state,
                updateContributionOptionResponse,
                isContribOptionUpdating: false,
                isContribOptionUpdated: true
            }
        },
        receiveContribOptionUpdateError: (state: IAccountState, action: PayloadAction<{ message: string }>) => {
            const { message } = action.payload;
            return {
                ...state,
                isContribOptionUpdating: false,
                isContribOptionUpdated: true,
                isUpdateContributionError: true,
                updateContributionErrorMessage: message
            }
        }
    }
})

const {
    resetState,
    requestManageAccounts,
    receiveManageAccounts,
    requestCMOptions,
    receiveCMOptions,
    receiveManageAccountsError,
    receiveCMOptionsError,
    requestRoutingValidation,
    receiveRoutingValidation,
    requestAccountUpdate,
    receiveAccountUpdate,
    receiveAccountUpdateError,
    requestResetErrorState,
    requestResetAccountState,
    requestContribOptionUpdate,
    receiveContribOptionUpdate,
    receiveContribOptionUpdateError
} = slice.actions;

function fetchBankInfo(umcID: string) {
    return async (dispatch: Dispatch<AnyAction>) => {

        dispatch(requestManageAccounts());

        try {

            const cmAccountInfoResponse = await client.post(new GetBankAccountName({ umcID: umcID }));

            //Check for errors
            if (cmAccountInfoResponse.responseStatus)
                throw cmAccountInfoResponse.responseStatus.message;

            dispatch(receiveManageAccounts({ cmAccountInfoResponse }));

        }
        catch (e) {
            dispatch(receiveManageAccountsError({ message: e }))
        }

        dispatch(requestCMOptions());

        try {
            const cmOptionResponse = await client.post(new CMOption({ umcId: umcID }));

            //Check for errors
            if (cmOptionResponse.responseStatus)
                throw cmOptionResponse.responseStatus.message;

            dispatch(receiveCMOptions({ cmOptionResponse }));

        }
        catch (e) {
            dispatch(receiveCMOptionsError({ message: e }))
        }
    }
}

function checkRoutingRumber(routingNumber: string, accountNumber: string, businessName: string) {
    return async (dispatch: Dispatch<AnyAction>) => {
        dispatch(requestRoutingValidation());

        try {

            const routingResponse = await client.post(new ValidateRoutingNumber({ routingNumber, accountNumber, businessName }));

            dispatch(receiveRoutingValidation({ routingResponse }));

        }
        catch (e) {
            dispatch(receiveManageAccountsError({ message: e }))
        }
    }
}

function updateBankAccountInfo(tran: string) {
    return async (dispatch: Dispatch<AnyAction>) => {

        dispatch(requestAccountUpdate());

        try {

            const addUpdateAccountResponse = await client.post(new UpdateBankAccountInfo({ tran: tran }));

            //Check for errors
            if (addUpdateAccountResponse.responseStatus)
                throw addUpdateAccountResponse.responseStatus.message;

            if (addUpdateAccountResponse.paymentinfo.status.toLowerCase() !== "received") {
                if (addUpdateAccountResponse?.paymentinfo.message.trim() !== "") {
                    throw addUpdateAccountResponse.paymentinfo.message;
                }
                else {
                    const unknownError = "Unknown Error";
                    throw unknownError;
                }
            }

            dispatch(receiveAccountUpdate({ addUpdateAccountResponse }));

        }
        catch (e) {
            dispatch(receiveAccountUpdateError({ message: e }))
        }
    }
}

function updateContributionOptions(activeInstitution: string, scheduleType: string, accountSequence: string, fileType: string, userName: string) {
    return async (dispatch: Dispatch<AnyAction>) => {

        dispatch(requestContribOptionUpdate());

        try {
            const updateContributionOptionResponse = await client.post(new UpdateContributionOption(
                {
                    org_id: activeInstitution,
                    scheduletype: scheduleType,
                    account_sequence: accountSequence,
                    filetype: fileType,
                    username: userName
                }
            ));

            //Check for errors
            if (updateContributionOptionResponse.responseStatus)
                throw updateContributionOptionResponse.responseStatus.message;

            dispatch(receiveContribOptionUpdate({ updateContributionOptionResponse }));
        }
        catch (e) {
            dispatch(receiveContribOptionUpdateError({ message: e }))
        }
    }
}

function resetAccountState() {
    return async (dispatch: Dispatch<AnyAction>) => {
        dispatch(requestResetAccountState());
    }
}

function resetAllStateAndRedirect() {
    return async (dispatch: Dispatch<AnyAction>) => {
        dispatch(requestResetAccountState());
        dispatch(push(Path.ContributionHistory));
    }
}

function resetErrorState() {
    return async (dispatch: Dispatch<AnyAction>) => {

        dispatch(requestResetErrorState());
    }
}

function cancelAddUpdate() {
    return async (dispatch: Dispatch<AnyAction>) => {

        dispatch(push(Path.ManageAccounts));
    }
}

export const accountReducer = slice.reducer;

export const accountActionCreators = {
    fetchBankInfo,
    cancelAddUpdate,
    checkRoutingRumber,
    updateBankAccountInfo,
    updateContributionOptions,
    resetErrorState,
    resetAccountState,
    resetState,
    resetAllStateAndRedirect
};